import "../css/header.scss";
import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiTruck,
  FiUser,
  FiX,
  FiChevronDown,
} from "react-icons/fi";
import { CartContext } from "./CartContext";

const navigationItems = [
  { label: "Cooking Ingredients", to: "/shop?category=cooking-ingredients" },
  { label: "Baking Ingredients", to: "/shop?category=baking-ingredients" },
  { label: "Spice Kits", to: "/shop?category=spice-kits" },
  { label: "Salts + Peppercorns", to: "/shop?category=salt-pepper" },
  { label: "Loose Leaf Teas", to: "/shop?category=tea" },
  { label: "Kitchenware", to: "/shop?category=kitchenware" },
  { label: "Gift Cards", to: "/shop?category=gift-cards" },
  { label: "Sale", to: "/shop?category=sale", accent: true },
  { label: "Wholesale", to: "/contact" },
];

const moreItems = [
  { label: "FAQs", to: "/faqs" },
  { label: "Policy Center", to: "/policies" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Cookie Policy", to: "/cookies" },
  { label: "Terms of Use", to: "/terms" },
  { label: "Returns", to: "/returns" },
];

function Header() {
  const { cartItems = [] } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const cartCount = Array.isArray(cartItems)
    ? cartItems.reduce(
        (total, item) => total + (Number(item?.quantity) || 0),
        0
      )
    : 0;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleLinkClick = () => {
    setMenuOpen(false);
    toTop();
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = new FormData(event.currentTarget).get("q")?.trim();

    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
    setMenuOpen(false);
    toTop();
  };

  return (
    <header className="site-header">
      <div className="site-header__topbar">
        <div className="site-header__container site-header__topbar-inner">
          <p className="site-header__topbar-message">
            <FiTruck aria-hidden="true" />
            <span>Secure delivery with a flat $9.99 shipping fee</span>
          </p>
        </div>
      </div>

      <div className="site-header__primary">
        <div className="site-header__container site-header__primary-inner">
          <button
            className="site-header__menu-button"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="site-navigation"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <FiX aria-hidden="true" />
            ) : (
              <FiMenu aria-hidden="true" />
            )}
          </button>

          <Link
            className="site-header__brand"
            to="/"
            aria-label="sickzuno home"
            onClick={handleLinkClick}
          >
            <img
              src="/images/brand/sickzuno-logo.png"
              alt="sickzuno"
              width="216"
              height="66"
            />
          </Link>

          <form
            className="site-header__search"
            role="search"
            onSubmit={handleSearch}
          >
            <label
              className="site-header__visually-hidden"
              htmlFor="site-search"
            >
              Search the store
            </label>
            <input
              id="site-search"
              name="q"
              type="search"
              placeholder="Search spices, herbs and ingredients..."
              autoComplete="off"
            />
            <button type="submit" aria-label="Search">
              <FiSearch aria-hidden="true" />
            </button>
          </form>

          <div className="site-header__actions">
            <Link
              className="site-header__action"
              to="/contact"
              onClick={handleLinkClick}
              aria-label="My account and customer care"
            >
              <FiUser aria-hidden="true" />
              <span className="site-header__action-copy">
                <small>Welcome</small>
                <strong>My Account</strong>
              </span>
            </Link>

            <Link
              className="site-header__action site-header__cart"
              to="/cartpage"
              onClick={handleLinkClick}
              aria-label={`Shopping cart, ${cartCount} ${
                cartCount === 1 ? "item" : "items"
              }`}
            >
              <span className="site-header__cart-icon">
                <FiShoppingBag aria-hidden="true" />
                <span className="site-header__cart-count" aria-hidden="true">
                  {cartCount}
                </span>
              </span>
              <span className="site-header__action-copy">
                <small>Your cart</small>
                <strong>
                  {cartCount === 1 ? "1 item" : `${cartCount} items`}
                </strong>
              </span>
            </Link>
          </div>
        </div>
      </div>

      <div className="site-header__nav-shell">
        <nav
          className={`site-header__nav${menuOpen ? " is-open" : ""}`}
          id="site-navigation"
          aria-label="Main navigation"
        >
          <ul className="site-header__container">
            {navigationItems.map(({ label, to, accent }) => (
              <li key={label}>
                <Link
                  className={accent ? "site-header__nav-accent" : undefined}
                  to={to}
                  onClick={handleLinkClick}
                >
                  {label}
                </Link>
              </li>
            ))}
            <li className="site-header__more">
              <details>
                <summary>
                  More <FiChevronDown aria-hidden="true" />
                </summary>
                <ul className="site-header__more-menu">
                  {moreItems.map(({ label, to }) => (
                    <li key={label}>
                      <Link to={to} onClick={handleLinkClick}>{label}</Link>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}

export default Header;
